class FriggTrueVariantProduct {
  constructor(section) {
    this.section = section;
    this.gallery = section.querySelector("[data-frigg-gallery]");
    this.dataElement = section.querySelector("[data-frigg-variant-data]");
    if (!this.gallery || !this.dataElement) return;

    try {
      this.data = JSON.parse(this.dataElement.textContent);
    } catch (error) {
      console.error("Unable to read FRIGG variant data.", error);
      return;
    }

    this.data.fallbackGallery = (this.data.fallbackGallery || []).filter(Boolean);
    this.variants = new Map(
      this.data.variants.map((variant) => [String(variant.id), variant]),
    );
    this.viewport = this.gallery.querySelector("[data-frigg-gallery-viewport]");
    this.thumbs = this.gallery.querySelector("[data-frigg-gallery-thumbs]");
    this.previousButton = this.gallery.querySelector("[data-frigg-gallery-previous]");
    this.nextButton = this.gallery.querySelector("[data-frigg-gallery-next]");
    this.status = this.gallery.querySelector("[data-frigg-gallery-status]");
    const initialVariant = this.variants.get(String(this.data.selectedVariantId));
    this.currentGallery = this.getGallery(initialVariant);
    this.activeIndex = 0;
    this.scrollFrame = null;
    this.snapTimer = null;
    this.dragState = null;
    this.touching = false;

    this.section.addEventListener("frigg:variant-change", (event) => {
      this.render(String(event.detail.variant.id));
    });
    this.thumbs?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-frigg-gallery-thumb]");
      if (!button) return;
      this.activateImage(Number(button.dataset.index));
    });
    this.previousButton?.addEventListener("click", () => {
      this.activateImage(this.activeIndex - 1);
    });
    this.nextButton?.addEventListener("click", () => {
      this.activateImage(this.activeIndex + 1);
    });
    this.viewport?.addEventListener("scroll", () => this.handleGalleryScroll(), {
      passive: true,
    });
    this.viewport?.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      this.activateImage(this.activeIndex + (event.key === "ArrowRight" ? 1 : -1));
    });
    this.viewport?.addEventListener("pointerdown", (event) => this.startDrag(event));
    this.viewport?.addEventListener("pointermove", (event) => this.moveDrag(event));
    this.viewport?.addEventListener("pointerup", (event) => this.endDrag(event));
    this.viewport?.addEventListener("pointercancel", (event) => this.endDrag(event));
    this.viewport?.addEventListener("touchstart", () => {
      this.touching = true;
      if (this.snapTimer) clearTimeout(this.snapTimer);
    }, { passive: true });
    this.viewport?.addEventListener("touchend", () => {
      this.touching = false;
      this.scheduleGallerySnap();
    }, { passive: true });
    this.viewport?.addEventListener("touchcancel", () => {
      this.touching = false;
      this.scheduleGallerySnap();
    }, { passive: true });

    this.resizeObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => this.positionActiveSlide())
      : null;
    if (this.viewport) this.resizeObserver?.observe(this.viewport);
    this.render(String(this.data.selectedVariantId));
  }

  render(id) {
    const variant = this.variants.get(String(id));
    if (!variant) return;

    const title = this.section.querySelector("[data-frigg-product-title]");
    if (title) title.textContent = variant.title;

    this.renderPrice(variant);
    this.renderAvailability(variant);
    this.renderDetails(variant.details || {});

    const submit = this.section.querySelector(".product-form__submit");
    if (submit) {
      submit.dataset.initialPrice = variant.priceFormatted;
      submit.dataset.initialPriceCents = String(variant.price || 0);
      const purchasable = variant.available && Number(variant.price) > 0;
      submit.disabled = !purchasable;
      submit.setAttribute("aria-disabled", String(!purchasable));
    }

    if (typeof window.syncStickyAddToCart === "function") {
      window.syncStickyAddToCart(variant);
    }

    const gallery = this.getGallery(variant);
    this.currentGallery = gallery;
    this.renderGallery(gallery, variant.title);
    this.gallery.dataset.selectedVariant = String(id);
  }

  renderPrice(variant) {
    const wrapper = this.section.querySelector(".product__price .price");
    if (!wrapper) return;

    const price = Number(variant.price);
    const compareAtPrice = Number(variant.compareAtPrice);
    const validPrice = Number.isFinite(price) && price > 0;
    const onSale = validPrice && compareAtPrice > price;
    const regular = wrapper.querySelector(".price__regular .price-item--regular");
    const sale = wrapper.querySelector(".price__sale .price-item--sale");
    const compare = wrapper.querySelector(".price__sale s.price-item--regular");

    wrapper.classList.toggle("price--unavailable", !validPrice);
    wrapper.classList.toggle("price--on-sale", onSale);
    if (regular) regular.textContent = validPrice ? variant.priceFormatted : "Price unavailable";
    if (sale) sale.textContent = validPrice ? variant.priceFormatted : "Price unavailable";
    if (compare) compare.textContent = onSale ? variant.compareAtPriceFormatted : "";
  }

  renderAvailability(variant) {
    const stock = this.section.querySelector("[data-frigg-stock]");
    if (!stock) return;
    const purchasable = variant.available && Number(variant.price) > 0;
    stock.classList.toggle("is-unavailable", !purchasable);
    stock.lastChild.textContent = purchasable
      ? " In stock"
      : variant.available
        ? " Price unavailable"
        : " Out of stock";
  }

  renderDetails(details) {
    this.section.querySelectorAll("[data-frigg-detail]").forEach((row) => {
      const value = this.formatDetailValue(details[row.dataset.friggDetail]);
      const output = row.querySelector("[data-frigg-detail-value]");
      row.hidden = !value;
      if (output) output.textContent = value;
    });
  }

  formatDetailValue(value) {
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    if (value && typeof value === "object") {
      return value.label || value.name || value.value || "";
    }
    return value == null ? "" : String(value);
  }

  getGallery(variant) {
    const variantGallery = Array.isArray(variant?.gallery) ? variant.gallery.filter(Boolean) : [];
    const generatedGallery = this.getGeneratedGallery(variant);
    const source = variantGallery.length
      ? variantGallery
      : generatedGallery.length
        ? generatedGallery
        : this.data.fallbackGallery;
    const seen = new Set();
    return source
      .map((image) => this.normalizeImage(image))
      .filter((image) => {
        if (!image?.url || seen.has(image.url)) return false;
        seen.add(image.url);
        return true;
      });
  }

  getGeneratedGallery(variant) {
    const sourceIds = window.FRIGG_VARIANT_GALLERY_SOURCES?.[String(variant?.id)];
    const galleries = window.FRIGG_GALLERY_SOURCES;
    if (!Array.isArray(sourceIds) || !galleries) return [];
    return sourceIds.flatMap((sourceId) => {
      const gallery = galleries[String(sourceId)];
      return Array.isArray(gallery) ? gallery.filter(Boolean) : [];
    });
  }

  normalizeImage(image) {
    if (!image) return null;
    if (typeof image === "string") return { url: image, thumbnailUrl: image };
    const url = image.url || image.preview_image?.src || image.src;
    return {
      url,
      thumbnailUrl: image.thumbnailUrl || url,
      alt: image.alt || "",
      width: image.width || 1000,
      height: image.height || 1000,
    };
  }

  renderGallery(gallery, title) {
    if (!gallery.length || !this.viewport || !this.thumbs) return;

    this.viewport.replaceChildren(
      ...gallery.map((image, index) => {
        const slide = document.createElement("figure");
        slide.className = "frigg-product-gallery__slide";
        slide.dataset.friggGallerySlide = "";
        slide.dataset.index = String(index);
        slide.setAttribute("aria-label", `Image ${index + 1}`);
        if (index !== 0) slide.setAttribute("aria-hidden", "true");

        const media = document.createElement("img");
        media.className = "frigg-product-gallery__main";
        media.src = image.url;
        media.alt = image.alt || title;
        media.width = image.width || 1000;
        media.height = image.height || 1000;
        media.loading = index === 0 ? "eager" : "lazy";
        if (index === 0) media.fetchPriority = "high";
        slide.append(media);
        return slide;
      }),
    );

    this.thumbs.replaceChildren(
      ...gallery.map((image, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = `frigg-product-gallery__thumb${index === 0 ? " is-active" : ""}`;
        button.dataset.friggGalleryThumb = "";
        button.dataset.index = String(index);
        button.setAttribute("aria-label", `View image ${index + 1}`);
        if (index === 0) button.setAttribute("aria-current", "true");

        const thumbnail = document.createElement("img");
        thumbnail.src = image.thumbnailUrl || image.url;
        thumbnail.alt = image.alt || title;
        thumbnail.width = 96;
        thumbnail.height = 96;
        thumbnail.loading = index < 5 ? "eager" : "lazy";
        button.append(thumbnail);
        return button;
      }),
    );
    this.viewport.scrollLeft = 0;
    this.updateGalleryState(0, false);
  }

  activateImage(index) {
    if (!this.viewport || !this.currentGallery?.length) return;
    const targetIndex = Math.max(0, Math.min(index, this.currentGallery.length - 1));
    this.viewport.scrollTo({
      left: targetIndex * this.viewport.clientWidth,
      behavior: "auto",
    });
    this.updateGalleryState(targetIndex);
  }

  handleGalleryScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const width = this.viewport?.clientWidth || 1;
      const index = Math.round((this.viewport?.scrollLeft || 0) / width);
      this.updateGalleryState(index);
      this.scheduleGallerySnap();
    });
  }

  scheduleGallerySnap() {
    if (this.snapTimer) clearTimeout(this.snapTimer);
    if (this.touching || this.dragState || !this.viewport) return;
    this.snapTimer = setTimeout(() => {
      this.snapTimer = null;
      const width = this.viewport?.clientWidth || 1;
      this.activateImage(Math.round((this.viewport?.scrollLeft || 0) / width));
    }, 140);
  }

  updateGalleryState(index, scrollThumb = true) {
    const total = this.currentGallery?.length || 0;
    this.activeIndex = total ? Math.max(0, Math.min(index, total - 1)) : 0;
    this.gallery.dataset.activeIndex = String(this.activeIndex);

    this.viewport
      ?.querySelectorAll("[data-frigg-gallery-slide]")
      .forEach((slide, slideIndex) => {
        if (slideIndex === this.activeIndex) slide.removeAttribute("aria-hidden");
        else slide.setAttribute("aria-hidden", "true");
      });

    this.thumbs
      .querySelectorAll("[data-frigg-gallery-thumb]")
      .forEach((button, buttonIndex) => {
        const active = buttonIndex === this.activeIndex;
        button.classList.toggle("is-active", active);
        if (active) button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      });

    this.previousButton?.toggleAttribute("disabled", this.activeIndex === 0 || total < 2);
    this.nextButton?.toggleAttribute("disabled", this.activeIndex >= total - 1 || total < 2);
    if (this.status) this.status.textContent = total ? `Image ${this.activeIndex + 1} of ${total}` : "";

    if (scrollThumb) {
      this.thumbs
        ?.querySelector(`[data-index="${this.activeIndex}"]`)
        ?.scrollIntoView({ behavior: "auto", block: "nearest", inline: "center" });
    }
  }

  startDrag(event) {
    if (event.pointerType !== "mouse" || event.button !== 0 || !this.viewport) return;
    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: this.viewport.scrollLeft,
    };
    this.viewport.setPointerCapture(event.pointerId);
    this.viewport.classList.add("is-dragging");
  }

  moveDrag(event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.viewport) return;
    event.preventDefault();
    this.viewport.scrollLeft = this.dragState.scrollLeft - (event.clientX - this.dragState.startX);
  }

  endDrag(event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.viewport) return;
    this.viewport.classList.remove("is-dragging");
    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }
    this.dragState = null;
    const width = this.viewport.clientWidth || 1;
    this.activateImage(Math.round(this.viewport.scrollLeft / width));
  }

  positionActiveSlide() {
    if (!this.viewport) return;
    this.viewport.scrollLeft = this.activeIndex * this.viewport.clientWidth;
  }
}

function initializeFriggTrueVariants(root = document) {
  root.querySelectorAll('[data-frigg-canonical="true"]').forEach((section) => {
    if (section.dataset.friggVariantsReady === "true") return;
    section.dataset.friggVariantsReady = "true";
    new FriggTrueVariantProduct(section);
  });
}

document.addEventListener("DOMContentLoaded", () => initializeFriggTrueVariants());
document.addEventListener("shopify:section:load", (event) =>
  initializeFriggTrueVariants(event.target),
);
